import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { StaffAccessContext } from "@/services/staffAccessContext"
import type { RealtimeStatus } from "@/services/realtime/realtimeClient"

export interface StaffSessionState {
  /** Opaque key issued/derived only after checking the server effective access context. */
  scopeKey: string | null
  epoch: number
  phase?: "inactive" | "checking" | "ready" | "unavailable"
  connectionStatus?: RealtimeStatus
}
const initialState: StaffSessionState = { scopeKey: null, epoch: 0, phase: "inactive" }

const slice = createSlice({
  name: "staffSession",
  initialState,
  reducers: {
    scopeEstablished(state, action: PayloadAction<string>) {
      if (state.scopeKey !== action.payload) {
        state.scopeKey = action.payload
        state.epoch++
      }
    },
    scopeRevoked(state) {
      state.scopeKey = null
      state.epoch++
      state.phase = "inactive"
    },
    connectionChanged(state, action: PayloadAction<RealtimeStatus>) { state.connectionStatus = action.payload },
    checkingStarted(state) { if (state.phase !== "unavailable") state.phase = "checking" },
    contextUnavailable(state) { state.phase = "unavailable" },
    contextAccepted(state, action: PayloadAction<StaffAccessContext>) {
      if (state.scopeKey !== action.payload.scopeKey) {
        state.scopeKey = action.payload.scopeKey
        state.epoch++
      }
      state.phase = "ready"
    },
  },
})

export const staffSessionActions = slice.actions
export default slice.reducer

export function currentStaffSession(state: unknown): StaffSessionState | null {
  if (!state || typeof state !== "object" || !("staffSession" in state)) return null
  const session = state.staffSession as StaffSessionState | null
  return session && typeof session.scopeKey === "string" && session.scopeKey.length > 0 &&
    Number.isSafeInteger(session.epoch) ? session : null
}

export function matchesStaffSession(state: unknown, expected: StaffSessionState): boolean {
  const session = currentStaffSession(state)
  return session !== null && session.scopeKey === expected.scopeKey && session.epoch === expected.epoch
}
